import React from "react";

import { Box } from "@mui/material";

import { Source } from "Constant/source";

import SourceMappingArea from "components/SourceMappingArea";

import AppCapiSourceForm from "./components/AppCapiSourceForm";
import CrmSourceForm from "./components/CrmSourceForm";
import DomainField from "./components/DomainField";
import HubSpotSourceForm from "./components/HubSpotSourceForm";
import SftpSourceForm from "./components/SftpSourceForm";
import SpreadsheetSourceForm from "./components/SpreadsheetSourceForm";
import UploadSourceForm from "./components/UploadSourceForm";
import UsageSelect from "./components/UsageSelect";
import WebSourceFields from "./components/WebSourceFields";
import ZohoSourceForm from "./components/ZohoSourceForm";
import {
    APP_CAPI_SOURCE_TYPES,
    CRM_SOURCE_TYPES,
    DOMAIN_FIELD_EXCLUDED_SOURCE_TYPES,
    SFTP_SOURCE_TYPES,
    USAGE_SELECT_SOURCE_TYPES,
    WEB_SOURCE_TYPES,
} from "./SourceCreation.constants";

const preventSubmit = (event) => event.preventDefault();

/**
 * Renders the source creation form (or the post-create mapping view) for the
 * state returned by `useSourceCreation`. The source type is picked in the
 * catalog step beforehand; the submit footer lives in `SourceCreationFooter`.
 */
const SourceCreationForm = ({ sourceCreation }) => {
    const {
        language,
        form,
        usage,
        mapping,
        zoho,
        hubspot,
        appCapi,
        sftp,
        spreadsheet,
        upload,
        setLoading,
        setTypeSelectBlocked,
        registerFooterAction,
        handleMappingSuccess,
        handleCrmClose,
        hostActions,
    } = sourceCreation;

    const { sourceType } = form;

    if (mapping.isMappingView) {
        return (
            <SourceMappingArea
                sourceId={mapping.mappingSourceId}
                sourceType={mapping.mappingSourceType}
                mappingSuccess={handleMappingSuccess}
                hideSaveButton
                registerFooterAction={registerFooterAction}
            />
        );
    }

    const isUpload = sourceType === Source.upload;
    const isSftp = SFTP_SOURCE_TYPES.includes(sourceType);

    return (
        <Box component="form" noValidate onSubmit={preventSubmit}>
            {USAGE_SELECT_SOURCE_TYPES.includes(sourceType) && (
                <UsageSelect
                    value={usage.usage}
                    options={usage.usageOptions}
                    onChange={usage.setUsage}
                />
            )}

            {!!sourceType &&
                !DOMAIN_FIELD_EXCLUDED_SOURCE_TYPES.includes(sourceType) && (
                    <DomainField
                        value={form.form.domain}
                        error={form.formErrors.domain}
                        language={language}
                        onChange={(value) => form.setField("domain", value)}
                    />
                )}

            {WEB_SOURCE_TYPES.includes(sourceType) && (
                <WebSourceFields
                    sourceType={sourceType}
                    language={language}
                    form={form}
                />
            )}

            {sourceType === Source.spreadsheet && (
                <SpreadsheetSourceForm
                    language={language}
                    spreadsheet={spreadsheet}
                    registerFooterAction={registerFooterAction}
                />
            )}

            {sourceType === Source.hub && (
                <HubSpotSourceForm
                    hubspot={hubspot}
                    registerFooterAction={registerFooterAction}
                />
            )}

            {isSftp && (
                <SftpSourceForm
                    sourceType={sourceType}
                    language={language}
                    sftp={sftp}
                />
            )}

            {isUpload && (
                <UploadSourceForm
                    language={language}
                    upload={upload}
                    usage={usage.usage}
                    setUsage={usage.setUsage}
                    leadMappings={zoho.leadMappings}
                    setLeadMappings={zoho.setLeadMappings}
                    openMapping={mapping.openMapping}
                    hostActions={hostActions}
                />
            )}

            {sourceType === Source.zoho && (
                <ZohoSourceForm
                    language={language}
                    zoho={zoho}
                    registerFooterAction={registerFooterAction}
                />
            )}

            {APP_CAPI_SOURCE_TYPES.includes(sourceType) && (
                <AppCapiSourceForm form={form.form} appCapi={appCapi} />
            )}

            {CRM_SOURCE_TYPES[sourceType] && (
                <CrmSourceForm
                    sourceType={sourceType}
                    hostActions={hostActions}
                    onClose={handleCrmClose}
                    setLoading={setLoading}
                    setTypeSelectBlocked={setTypeSelectBlocked}
                    registerFooterAction={registerFooterAction}
                />
            )}
        </Box>
    );
};

export default SourceCreationForm;
